import { API_BASE, authenticatedFetch, validateCallData } from '../../utils';

/** The assistant's HTTP calls. Errors carry the server's own sentence when it sent one. */

const readError = async (response, fallback) => {
  try {
    const body = await response.json();
    if (typeof body?.detail === 'string') return body.detail;
  } catch {
    /* not JSON */
  }
  return fallback;
};

const post = async (path, body, fallback) => {
  const response = await authenticatedFetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!response.ok) throw new Error(await readError(response, fallback));
  return response.json();
};

const ANSWER_FAILED = "The assistant couldn't answer just now. Try again in a moment.";

/**
 * One turn, streamed: `onEvent` is called with each event as it arrives (see
 * Backend/assistant/agent.py `stream`): thinking, step, block, text, then done,
 * or error. Resolves when the stream ends; `signal` stops it.
 */
export const streamAssistant = async (messages, context, onEvent, signal) => {
  const response = await authenticatedFetch(`${API_BASE}/api/assistant/chat/stream`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ messages, context }),
    signal,
  });
  if (!response.ok) throw new Error(await readError(response, ANSWER_FAILED));

  const emit = (line) => {
    const trimmed = line.trim();
    if (!trimmed) return;
    try {
      onEvent(JSON.parse(trimmed));
    } catch {
      /* a partial or foreign line: skip it rather than end the answer */
    }
  };

  // Without a readable body (old browsers), the whole answer arrives at once.
  if (!response.body?.getReader) {
    (await response.text()).split('\n').forEach(emit);
    return;
  }
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  for (;;) {
    // eslint-disable-next-line no-await-in-loop
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let newline = buffer.indexOf('\n');
    while (newline >= 0) {
      emit(buffer.slice(0, newline));
      buffer = buffer.slice(newline + 1);
      newline = buffer.indexOf('\n');
    }
  }
  emit(buffer + decoder.decode());
};

export const approveAction = (id) =>
  post(`/api/assistant/actions/${encodeURIComponent(id)}/approve`, undefined, "That couldn't be applied. Try again.");

export const cancelAction = (id) =>
  post(`/api/assistant/actions/${encodeURIComponent(id)}/cancel`, undefined, "That couldn't be dismissed. Try again.");

export const undoAction = (id) =>
  post(`/api/assistant/actions/${encodeURIComponent(id)}/undo`, undefined, "That couldn't be undone.");

/** One full call row, for opening the call drawer from a row in the chat. */
export const fetchCall = async (callId) => {
  const params = new URLSearchParams({ call_ids: callId, limit: '1', offset: '0' });
  const response = await authenticatedFetch(`${API_BASE}/calls?${params}`);
  if (!response.ok) throw new Error("Couldn't open that call.");
  const [call] = validateCallData(await response.json());
  if (!call) throw new Error("That call isn't one you can open.");
  return call;
};
